import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, queryOptions, type QueryFunction } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { StatusPill } from "@/components/portal/StatusPill";
import { useAuthedMutation } from "@/lib/authed-mutation";
import { adminOrderQuery, INVALIDATE, globalQueryFn } from "@/lib/query-registry";
import {
  updateOrder,
  deliverReport,
  retryAudit,
  getAdminReportDownload,
} from "@/lib/orders.functions";
import type { AdminOrder } from "@/lib/orders.functions";
import { adminAuditSectionsQuery, adminOrderQueueStatusQuery } from "@/lib/query-registry";
import type { AdminAuditSection } from "@/lib/orders.functions";
import type { OrderQueueStatus } from "@/lib/orders.functions";
import type { ReportAccessLogEntry } from "@/lib/report-settings.functions";
import { ReportEditor } from "@/components/admin/ReportEditor";

export const Route = createFileRoute("/admin/orders/$id")({
  component: AdminOrderDetailPage,
});

const STATUS_OPTIONS = [
  "paid",
  "processing",
  "in_review",
  "review_needed",
  "ready",
  "complete",
  "failed",
  "refunded",
] as const;
type EditableStatus = (typeof STATUS_OPTIONS)[number];

function formatPrice(cents: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function formatDate(iso: string | null) {
  if (!iso) return ", ";
  return new Date(iso).toLocaleString();
}

function AdminOrderDetailPage() {
  const { id } = Route.useParams();
  const q = useQuery(adminOrderQuery(id));
  const order = (q.data as { order?: AdminOrder } | undefined)?.order;

  const [status, setStatus] = useState<EditableStatus>("processing");
  const [reportUrl, setReportUrl] = useState("");
  const [saved, setSaved] = useState(false);
  const [deliverNote, setDeliverNote] = useState<string | null>(null);

  useEffect(() => {
    if (!order) return;
    const initial = STATUS_OPTIONS.includes(order.status as EditableStatus)
      ? (order.status as EditableStatus)
      : "processing";
    setStatus(initial);
    setReportUrl(order.report_url ?? "");
  }, [order]);

  const m = useAuthedMutation(updateOrder, {
    invalidate: [INVALIDATE.adminOrders, ["admin.order.get", { id }]],
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    },
  });

  const deliverM = useAuthedMutation(deliverReport, {
    invalidate: [
      INVALIDATE.adminOrders,
      ["admin.order.get", { id }],
      ["admin.report-access-log.list", { order_id: id }],
    ],
    onSuccess: (res) => {
      const r = res as { alreadyDelivered: boolean; email_sent_to?: string };
      setDeliverNote(
        r.alreadyDelivered
          ? "Already delivered."
          : `Delivered to ${r.email_sent_to}. Status flipped to complete.`,
      );
      setTimeout(() => setDeliverNote(null), 4000);
    },
  });

  const retryM = useAuthedMutation(retryAudit, {
    invalidate: [
      INVALIDATE.adminOrders,
      ["admin.order.get", { id }],
      ["admin.order.queue-status", { order_id: id }],
    ],
    onSuccess: () => toast.success("Queued, audit will run within ~1 min."),
    onError: (e) => toast.error(e.message),
  });

  const previewM = useAuthedMutation(getAdminReportDownload, {
    invalidate: [["admin.report-access-log.list", { order_id: id }]],
    onSuccess: (res) => {
      const r = res as { url: string };
      if (r?.url) window.open(r.url, "_blank", "noopener,noreferrer");
    },
  });

  const sectionsQ = useQuery({
    ...adminAuditSectionsQuery(id),
    enabled: Boolean(order),
  });

  const queueStatusQ = useQuery({
    ...adminOrderQueueStatusQuery(id),
    enabled: Boolean(order) && (order?.status === "queued" || order?.status === "processing"),
  });

  const accessLogQ = useQuery({
    ...queryOptions({
      queryKey: ["admin.report-access-log.list", { order_id: id }] as const,
      queryFn: globalQueryFn as QueryFunction<{ entries: ReportAccessLogEntry[] }>,
      staleTime: 30 * 1000,
    }),
    enabled: Boolean(order),
  });

  if (q.isPending) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (q.isError || !order) {
    return (
      <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-6 text-sm text-destructive">
        {q.error?.message ?? "Order not found"}
      </div>
    );
  }

  const onSave = (e: React.FormEvent) => {
    e.preventDefault();
    // If admin is flipping to complete via dropdown, route through deliverReport
    // so the customer gets the final email + access log + idempotency.
    if (status === "complete" && order?.status !== "complete") {
      deliverM.mutate({ order_id: id });
      // Also persist any report_url edit alongside (deliverReport doesn't touch it).
      if (reportUrl.trim() !== (order?.report_url ?? "")) {
        m.mutate({ id, report_url: reportUrl.trim() });
      }
      return;
    }
    m.mutate({ id, status, report_url: reportUrl.trim() });
  };

  const canDeliver =
    !!order &&
    (order.status === "review_needed" || order.status === "ready") &&
    !!(order.report_url && order.report_url.trim());

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/admin/orders" className="text-xs text-muted-foreground hover:text-foreground">
            ← All orders
          </Link>
          <h1 className="mt-2 font-display text-2xl font-semibold text-foreground">
            {order.order_number}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            ShugertGEO, {order.plan_name} · {formatPrice(order.amount_cents, order.currency)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {order.is_internal_audit && (
            <span
              className="rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em]"
              style={{ borderColor: "#C8A84B", color: "#C8A84B" }}
              title="Internal audit: free, no payment, customer emails suppressed by default."
            >
              Internal
            </span>
          )}
          <StatusPill status={order.status} />
        </div>
      </div>

      <QueueStatusBadge orderStatus={order.status} data={queueStatusQ.data} />

      <form onSubmit={onSave} className="space-y-4 rounded-2xl border border-border bg-card/30 p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-base font-semibold text-foreground">Fulfillment</h2>
          {order.plan_id === "perf_teardown" && (
            <Link
              to="/admin/templates/teardown-delivery"
              className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Open delivery template →
            </Link>
          )}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="status"
              className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground"
            >
              Status
            </label>
            <select
              id="status"
              value={status}
              onChange={(e) => setStatus(e.target.value as EditableStatus)}
              className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:border-foreground focus:outline-none"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="report-url"
              className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground"
            >
              Report URL
            </label>
            <input
              id="report-url"
              type="url"
              placeholder="https://…"
              value={reportUrl}
              onChange={(e) => setReportUrl(e.target.value)}
              className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:border-foreground focus:outline-none"
            />
          </div>
        </div>
        {m.isError && (
          <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {m.error.message}
          </p>
        )}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={m.isPending || deliverM.isPending}
            className="rounded-full bg-foreground px-5 py-2 text-sm font-medium text-background hover:opacity-90 disabled:opacity-50"
          >
            {m.isPending || deliverM.isPending ? "Saving…" : "Save"}
          </button>
          {saved && <span className="text-xs text-muted-foreground">Saved ✓</span>}
          {deliverNote && <span className="text-xs text-muted-foreground">{deliverNote}</span>}
        </div>
      </form>

      {order && order.status !== "complete" && (
        <div className="space-y-3 rounded-2xl border border-border bg-card/30 p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-display text-base font-semibold text-foreground">
                Deliver report
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Sends the final confidential delivery email with a signed download link, logs the
                access, and marks the order complete.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                if (!canDeliver) return;
                if (!confirm(`Send the final report to ${order.email}?`)) return;
                deliverM.mutate({ order_id: id });
              }}
              disabled={!canDeliver || deliverM.isPending}
              title={
                canDeliver
                  ? "Send the final delivery email"
                  : "Set status to review_needed or ready and attach a report URL first."
              }
              className="rounded-full bg-foreground px-5 py-2 text-sm font-medium text-background hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {deliverM.isPending ? "Sending…" : "Deliver to client"}
            </button>
          </div>
          {deliverM.isError && (
            <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {deliverM.error.message}
            </p>
          )}
        </div>
      )}

      {order &&
        ["failed", "review_needed", "ready", "complete", "processing"].includes(order.status) && (
          <div
            className={`space-y-3 rounded-2xl border p-6 ${order.status === "failed" ? "border-destructive/40 bg-destructive/5" : "border-border bg-card/30"}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-base font-semibold text-foreground">
                  {order.status === "failed" ? "Retry audit" : "Regenerate report"}
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Re-runs the crawl + LLM pipeline and overwrites the structured report content.
                  Does NOT re-send any customer emails.
                </p>
                {order.error_message && (
                  <p className="mt-2 rounded border border-destructive/30 bg-background/40 px-3 py-2 font-mono text-[11px] text-destructive">
                    {order.error_message}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  if (
                    !confirm(
                      `Regenerate the report for ${order.website_url}? This overwrites any current report content.`,
                    )
                  )
                    return;
                  retryM.mutate({ order_id: id });
                }}
                disabled={retryM.isPending}
                className={`rounded-full px-5 py-2 text-sm font-medium disabled:opacity-40 ${order.status === "failed" ? "bg-destructive text-destructive-foreground" : "bg-foreground text-background hover:opacity-90"}`}
              >
                {retryM.isPending
                  ? "Queuing…"
                  : order.status === "failed"
                    ? "Retry audit"
                    : "Regenerate report"}
              </button>
            </div>
            {retryM.isError && (
              <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {retryM.error.message}
              </p>
            )}
          </div>
        )}

      {order && order.report_url && (
        <div className="space-y-3 rounded-2xl border border-border bg-card/30 p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-display text-base font-semibold text-foreground">Report file</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Opens the generated .docx in a new tab with a 1-hour signed link. Every preview is
                logged below.
              </p>
            </div>
            <button
              type="button"
              onClick={() => previewM.mutate({ order_id: id })}
              disabled={previewM.isPending}
              className="rounded-full border border-foreground px-5 py-2 text-sm font-medium text-foreground hover:bg-foreground hover:text-background disabled:opacity-40"
            >
              {previewM.isPending ? "Signing…" : "Open report (.docx)"}
            </button>
            <a
              href={`/admin/orders/${id}/report-preview`}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full border border-border px-5 py-2 text-sm font-medium text-foreground hover:bg-card"
              title="Vista previa interna: no es el entregable al cliente"
            >
              Preview interno (HTML)
            </a>
          </div>
          {previewM.isError && (
            <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {previewM.error.message}
            </p>
          )}
        </div>
      )}

      <AuditSectionsCard sections={sectionsQ.data?.sections} loading={sectionsQ.isPending} />

      <ReportEditor orderId={id} />

      <details className="rounded-2xl border border-border bg-card/30 p-6">
        <summary className="cursor-pointer font-display text-base font-semibold text-foreground">
          Access log
        </summary>
        <div className="mt-4 text-xs text-muted-foreground">
          {accessLogQ.isPending && <p>Loading…</p>}
          {accessLogQ.isError && <p className="text-destructive">{accessLogQ.error.message}</p>}
          {accessLogQ.data?.entries?.length === 0 && <p>No accesses yet.</p>}
          {!!accessLogQ.data?.entries?.length && (
            <ul className="divide-y divide-border">
              {accessLogQ.data.entries.map((e) => (
                <li key={e.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2">
                  <span className="font-mono">{new Date(e.accessed_at).toLocaleString()}</span>
                  <span className="rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-wider text-foreground">
                    {e.source}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {e.signed_url_expires_at
                      ? `expires ${new Date(e.signed_url_expires_at).toLocaleString()}`
                      : ", "}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </details>

      <div className="grid gap-4 rounded-2xl border border-border bg-card/30 p-6">
        <h2 className="font-display text-base font-semibold text-foreground">Customer</h2>
        <DetailRow label="Name" value={order.full_name} />
        <DetailRow label="Email" value={order.email} />
        <DetailRow label="Company" value={order.company ?? ", "} />
        <DetailRow label="Industry" value={order.industry ?? ", "} />
        <DetailRow label="Website" value={order.website_url} />
      </div>

      <div className="grid gap-4 rounded-2xl border border-border bg-card/30 p-6">
        <h2 className="font-display text-base font-semibold text-foreground">Intake</h2>
        <DetailRow label="Target audience" value={order.target_audience ?? ", "} />
        <DetailRow label="Competitors" value={order.competitors ?? ", "} />
        <DetailRow label="Notes" value={order.notes ?? ", "} multiline />
      </div>

      <div className="grid gap-4 rounded-2xl border border-border bg-card/30 p-6 text-xs text-muted-foreground">
        <h2 className="font-display text-base font-semibold text-foreground">Payment</h2>
        <DetailRow label="Paid at" value={formatDate(order.paid_at)} />
        <DetailRow label="ETA" value={formatDate(order.estimated_delivery_at)} />
        <DetailRow label="Stripe session" value={order.stripe_session_id ?? ", "} mono />
        <DetailRow label="Payment intent" value={order.stripe_payment_intent ?? ", "} mono />
      </div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono,
  multiline,
}: {
  label: string;
  value: string;
  mono?: boolean;
  multiline?: boolean;
}) {
  return (
    <div className="grid gap-1 sm:grid-cols-[180px,1fr] sm:items-baseline sm:gap-4">
      <dt className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</dt>
      <dd
        className={[
          "text-sm text-foreground",
          mono ? "font-mono break-all text-xs" : "",
          multiline ? "whitespace-pre-wrap" : "break-words",
        ].join(" ")}
      >
        {value}
      </dd>
    </div>
  );
}

function AuditSectionsCard({
  sections,
  loading,
}: {
  sections: AdminAuditSection[] | undefined;
  loading: boolean;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card/30 p-6">
      <h2 className="font-display text-base font-semibold text-foreground">Audit sections</h2>
      {loading ? (
        <p className="mt-4 text-xs text-muted-foreground">Loading…</p>
      ) : !sections || sections.length === 0 ? (
        <p className="mt-4 text-xs text-muted-foreground">
          No section scores yet, the audit hasn't finished.
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-border">
          {sections.map((s) => {
            const max = s.max_score ?? 0;
            const score = s.score ?? 0;
            const pct = max > 0 ? Math.min(100, Math.round((score / max) * 100)) : 0;
            return (
              <li key={s.id} className="grid items-center gap-3 py-3 sm:grid-cols-[1fr,80px,1fr]">
                <span className="text-sm text-foreground">{s.section}</span>
                <span className="font-mono text-xs text-muted-foreground">
                  {s.score ?? ", "}/{s.max_score ?? ", "}
                </span>
                <div className="h-2 overflow-hidden rounded-full bg-background">
                  <div className="h-full bg-foreground" style={{ width: `${pct}%` }} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function QueueStatusBadge({
  orderStatus,
  data,
}: {
  orderStatus: string;
  data: OrderQueueStatus | undefined;
}) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (orderStatus !== "processing") return;
    const t = setInterval(() => setTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [orderStatus]);
  void tick;

  if (orderStatus === "queued") {
    const pos = data?.position ?? null;
    return (
      <div className="rounded-2xl border border-amber-400/40 bg-amber-400/10 px-4 py-3 text-sm text-amber-200">
        <div className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full bg-amber-400" />
          <span className="font-medium">Position in queue: #{pos ?? "…"}</span>
          <span className="text-xs text-amber-200/80"> Dispatcher runs every minute</span>
        </div>
      </div>
    );
  }

  if (orderStatus === "processing") {
    const startedAt = data?.started_at;
    let elapsedLabel = ", ";
    if (startedAt) {
      const secs = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
      const m = Math.floor(secs / 60);
      const s = secs % 60;
      elapsedLabel = `${m}m ${s}s`;
    }
    return (
      <div className="rounded-2xl border border-sky-400/40 bg-sky-400/10 px-4 py-3 text-sm text-sky-200">
        <div className="flex items-center gap-2">
          <span className="relative inline-flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-sky-400" />
          </span>
          <span className="font-medium">Processing now… {elapsedLabel} elapsed</span>
        </div>
        {data?.stage && (
          <div className="mt-1 pl-4 text-xs text-sky-200/80">Stage: {data.stage}</div>
        )}
      </div>
    );
  }

  return null;
}
